import { useEffect, useRef, useState } from 'react';
import logo from '../../assets/logo.svg';
import vector from '../../assets/vector.png';
import { AppInfo } from '../../types';
import {
    DownloadButton, DropdownButton, DropdownContainer, DropdownItem, DropdownMenu,
    HeaderAppDesc, HeaderAppTitle, HeaderContainer, LoadingIndicator,
    ModalButtonGroup, ModalCloseButton, ModalContent, ModalDescription,
    ModalDownloadButton, ModalDependenciesButton, ModalOverlay, ModalTitle
} from './Header.styled';

const Header = ({ appInfo }: { appInfo?: AppInfo }) => {
    const [version, setVersion] = useState<string>('');
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function init() {
            if (!version && appInfo?.version) {
                setVersion(appInfo.version);
                setLoading(false);
            }
        }

        init();
    }, [appInfo, version]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const baseUrl = Boolean(version)
        ? `https://localapps.cdmvision.com/supar/build/supar-composer/supar-composer-${version}`
        : null;

    const dependenciesUrl = 'https://localapps.cdmvision.com/supar/SuPAR Composer 2025 Dependencies.zip';

    return (
        <HeaderContainer>
            <img src={logo} alt="SuPAR Composer Logo" />
            <HeaderAppTitle>SuPAR Composer</HeaderAppTitle>
            <HeaderAppDesc>An Inspection Tool with AR</HeaderAppDesc>
            <DownloadButton
                $isLoading={isLoading}
                disabled={isLoading}
                onClick={() => { window.location.href = `${baseUrl}.exe`; }}
            >
                {!isLoading && <img src={vector} alt="Download Icon" />}
                {isLoading ? <LoadingIndicator /> : `Download ${version}`}
            </DownloadButton>
            <DropdownContainer ref={dropdownRef}>
                <DropdownButton onClick={toggleDropdown} disabled={isLoading}>
                    Other versions
                </DropdownButton>
                <DropdownMenu $isOpen={isDropdownOpen}>
                    <DropdownItem
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `${baseUrl}.zip`;
                            setIsDropdownOpen(false);
                        }}
                    >
                        <img src={vector} alt="Download Icon" />
                        Installer (.zip)
                    </DropdownItem>
                    <DropdownItem
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `${baseUrl}_portable.zip`;
                            setIsDropdownOpen(false);
                        }}
                    >
                        <img src={vector} alt="Download Icon" />
                        Portable (.zip)
                    </DropdownItem>
                    <DropdownItem
                        onClick={(e) => {
                            e.preventDefault();
                            setIsModalOpen(true);
                            setIsDropdownOpen(false);
                        }}
                    >
                        Enterprise Installer (.msi)
                    </DropdownItem>
                </DropdownMenu>
            </DropdownContainer>

            <ModalOverlay
                $isOpen={isModalOpen}
                onClick={() => setIsModalOpen(false)}
            >
                <ModalContent onClick={(e) => e.stopPropagation()}>
                    <ModalTitle>Enterprise Installer</ModalTitle>
                    <ModalButtonGroup>
                        <ModalDescription>
                            Enterprise installer formats are optimized for enterprise environments. The MSI format can be used by system administrators for mass installation and deployment.
                        </ModalDescription>
                        <ModalDownloadButton
                            onClick={() => {
                                window.location.href = `${baseUrl}.msi`;
                                setIsModalOpen(false);
                            }}
                        >
                            <img src={vector} alt="Download Icon" />
                            Download {version} (.msi)
                        </ModalDownloadButton>
                        <ModalDescription>
                            To ensure SuPAR Composer functions correctly, the following dependencies must be installed on your system. Please download and install these prerequisite files before running SuPAR Composer.
                        </ModalDescription>
                        <ModalDependenciesButton
                            onClick={() => {
                                window.location.href = dependenciesUrl;
                                setIsModalOpen(false);
                            }}
                        >
                            <img src={vector} alt="Download Icon" />
                            Download dependencies (.zip)
                        </ModalDependenciesButton>
                        <ModalCloseButton onClick={() => setIsModalOpen(false)}>
                            Close
                        </ModalCloseButton>
                    </ModalButtonGroup>
                </ModalContent>
            </ModalOverlay>
        </HeaderContainer>
    );
}

export default Header;
